import { useTranslation } from 'react-i18next';
import Accordion from 'web-library/stories/atom/Accordion/Accordion';
import TextFieldForm from 'web-library/stories/atom/TextField/TextFieldForm';
import React from 'react';
import { UseFormReturn } from 'react-hook-form';
import { nameRegExp } from 'web-library';
import { useStyles } from './PersonalParticulars';
import {
  CandidateProfileFormData,
  CandidateProfileFormDefaultValues,
  CandidateProfileIsDisableInputs,
} from '../../../../models';
import {
  getIExamDropdownItemLabel,
  getIExamMasterCodeSelected,
} from '../../logic/useCreateCandidateForm';
import { ContactNumberFormItem } from '../../../../components/form-items/ContactNumberFormItem';
import ControlledAutocomplete from '../../../../components/form-items/ControlledAutocomplete';
import { useGetIExamMasterCode } from '../../../../api-refactored/user/useGetIExamMasterCode';

export interface NextOfKinDetailsProps {
  formHook: UseFormReturn<CandidateProfileFormData>;
  formDefaultValues?: CandidateProfileFormDefaultValues;
  isDisableInputs?: CandidateProfileIsDisableInputs;
  isIExamsApiEnabled?: boolean;
}

const NextOfKinDetails = ({
  formHook,
  formDefaultValues,
  isDisableInputs,
  isIExamsApiEnabled = true,
}: NextOfKinDetailsProps) => {
  const { t } = useTranslation();
  const { classes } = useStyles();
  const { control, formState:{errors}, setValue } = formHook;

  const {
    data: dataIEXAM,
    isLoading: isLoadingIEXAM,
    error: errorMasterCode,
    refetch: refetchMasterCode,
  } = useGetIExamMasterCode({ enabled: isIExamsApiEnabled });

  return (
    <Accordion
      defaultExpanded
      header={t('profile->>CreateCandidateForm.NextOfKinDetails')}
      bottomLine={false}
    >
      <TextFieldForm
        form={formHook}
        isInputTextUpperCase
        onChange={(e) => setValue('nextOfKinName', e.target.value.toUpperCase())}
        label={t('profile->>CreateCandidateForm.Name')}
        isRequired
        placeholder={formDefaultValues?.nextOfKinName || 'Enter next-of-kin name'}
        name="nextOfKinName"
        id="input-nextOfKinName"
        type="text"
        error={!!errors.nextOfKinName}
        helperText={errors?.nextOfKinName?.message?.toString()}
        fullWidth
        className={classes.textField}
        disabled={isDisableInputs?.nextOfKinName}
        regExpToBlock={nameRegExp}
      />
      <ControlledAutocomplete
        label={t('profile->>CreateCandidateForm.RelationshipToCandidate')}
        isRequired
        control={control}
        name="nextOfKinRelationshipToCandidate"
        id="input-select-nextOfKinRelationshipToCandidate"
        error={!!errors.nextOfKinRelationshipToCandidate}
        helperText={errors?.nextOfKinRelationshipToCandidate?.message?.toString()}
        loading={isLoadingIEXAM}
        loadingError={!!errorMasterCode}
        loadingErrorOnReFetch={refetchMasterCode}
        options={dataIEXAM ? dataIEXAM.relationship : []}
        getOptionLabel={(option) => typeof option === 'string' ? option : getIExamDropdownItemLabel(option)}
        placeholder={
          formDefaultValues?.nextOfKinRelationshipToCandidate?.codeValueDescription ||
          'Select relationship to candidate'
        }
        disabled={isDisableInputs?.nextOfKinRelationshipToCandidate}
        isOptionEqualToValue={getIExamMasterCodeSelected}
        className={classes.textField}
      />
      <ContactNumberFormItem
        containerClassName={classes.textFieldSmallPaddingBottom}
        id="nextOfKinCountryCode"
        formHook={formHook}
        label={t('profile->>CreateCandidateForm.EmergencyContactNo')}
        isRequired
        countryCode={{
          name: 'nextOfKinCountryCode',
          error: !!errors.nextOfKinCountryCode,
          helperText: errors?.nextOfKinCountryCode?.message?.toString(),
          disabled: isDisableInputs?.nextOfKinCountryCode,
        }}
        countryCodeisOptionEqualToValue={getIExamMasterCodeSelected}
        number={{
          name: 'nextOfKinContactNumber',
          error: !!errors.nextOfKinContactNumber,
          helperText: errors?.nextOfKinContactNumber?.message?.toString(),
          disabled: isDisableInputs?.nextOfKinContactNumber,
        }}
        isSkipTransform
        placeholder={formDefaultValues?.nextOfKinContactNumber || 'Enter emergency contact no.'}
        isIExamsApiEnabled={isIExamsApiEnabled}
      />
    </Accordion>
  );
};

export default NextOfKinDetails;
